import {
    type FC,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { usePermissions } from "../../../Utilities/Domain/Users/Permissions";
import { File } from "./File";
import { FileLoading } from "./FileLoading";
import {
    getFile,
    type MediaDirectoryType,
    type MediaFilePages,
    MediaItemType,
    type MediaPathType,
    type MediaSelectedType,
} from "./MediaUtilities";

interface Props {
    current: Array<MediaPathType>;
    directory: MediaDirectoryType;
    items: MediaFilePages;
    onLoadMore: (index: number) => Promise<void>;
    onWrapperClick: (e: MouseEvent<HTMLDivElement>) => void;
    onClick: (e: MouseEvent, index: number, item: MediaSelectedType) => void;
    selected: Array<MediaSelectedType>;
    small?: boolean;
    multi: boolean;
    onToggle: (item: MediaSelectedType) => void;
}

const permissions = [ "create_media_folder" ] as const;

const MIN_COLUMN_SIZE = 200;
const MIN_SMALL_COLUMN_SIZE = 100;

export const FileGrid: FC<Props> = ({
    current,
    directory,
    items,
    onWrapperClick,
    onLoadMore,
    onClick,
    selected,
    small,
    multi,
    onToggle,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { createMediaFolder } = usePermissions(permissions);

    const [ columns, setColumns ] = useState(1);
    const [ display, setDisplay ] = useState(false);

    useEffect(() => {
        const column = small ? MIN_SMALL_COLUMN_SIZE : MIN_COLUMN_SIZE;

        const observer = new ResizeObserver((entries) => {
            if(!entries.length) {
                return;
            }

            setColumns(Math.trunc(entries[0].contentRect.width / column));
        });

        if(wrapperRef.current) {
            observer.observe(wrapperRef.current);

            setDisplay(true);
            setColumns(Math.trunc(wrapperRef.current.clientWidth / column));
        }

        return () => {
            observer.disconnect();
        };
    }, [ small ]);

    let folderCount = directory.folders.length;
    let folderUpIndex: false | number = false;

    if(current.length) {
        folderCount++;
        folderUpIndex = 0;
    }

    const canCreateFolder = current?.[0]?.id === "home" && createMediaFolder;
    let createFolderIndex: false | number = false;

    if(canCreateFolder) {
        folderCount++;
        createFolderIndex = (folderUpIndex === false) ? 0 : 1;
    }

    const folderRows = Math.ceil(folderCount / columns);
    const itemRows = Math.ceil(directory.fileCount / columns);

    const virtualizer = useVirtualizer({
        count           : folderRows + itemRows,
        getScrollElement: () => wrapperRef.current,
        estimateSize    : () => small ? 86 : 200,
    });

    useEffect(() => {
        virtualizer.measure();
    }, [ virtualizer, small ]);

    return (
        <div
            ref={wrapperRef}
            className="file-wrapper"
            onClick={onWrapperClick}
        >
            {display && (
                <div
                    className="w-full relative"
                    style={{
                        height: `${virtualizer.getTotalSize()}px`,
                    }}
                >
                    {virtualizer.getVirtualItems().map(item => {
                        const content: Array<ReactNode> = [];

                        if(item.index < folderRows) {
                            // Render folder row
                            const offset = item.index * columns;

                            for(let i = 0; i < columns; i++) {
                                const index = offset + i;

                                if(index >= folderCount) {
                                    break;
                                }

                                if(index === folderUpIndex) {
                                    content.push(
                                        <File
                                            key="folder-up"
                                            index={-1}
                                            id="..."
                                            name="..."
                                            type={MediaItemType.Folder}
                                            onClick={onClick}
                                            selected={false}
                                            navUp
                                            connections={[]}
                                        />
                                    );
                                } else if(index === createFolderIndex) {
                                    content.push(
                                        <File
                                            key="new-folder"
                                            index={-2}
                                            id="new-folder"
                                            name="New Folder"
                                            type={MediaItemType.Folder}
                                            onClick={onClick}
                                            selected={false}
                                            newFolder
                                            connections={[]}
                                        />
                                    );
                                } else {
                                    const relativeIndex = index -
                                        (folderUpIndex === false ? 0 : 1) -
                                        (createFolderIndex === false ? 0 : 1);

                                    const folder = directory.folders[relativeIndex];

                                    const isSelected = selected.length === 1 ? selected[0].id === folder.uuid : false;

                                    content.push(
                                        <File
                                            key={folder.name + "-" + relativeIndex}
                                            id={folder.uuid}
                                            index={relativeIndex}
                                            name={folder.name}
                                            type={MediaItemType.Folder}
                                            selected={isSelected}
                                            onClick={onClick}
                                            connections={[]}
                                        />
                                    );
                                }
                            }
                        } else {
                            // Render item row
                            const offset = (item.index - folderRows) * columns;

                            for(let i = 0; i < columns; i++) {
                                const index = offset + i;

                                if(index >= directory.fileCount) {
                                    break;
                                }

                                const item = getFile(index, items);

                                if(!item) {
                                    content.push(
                                        <FileLoading
                                            key={index}
                                            index={index}
                                            onLoad={onLoadMore}
                                        />
                                    );
                                } else {
                                    const isSelected = selected.find(sItem => sItem.id === item.uuid) !== undefined;

                                    content.push(
                                        <File
                                            id={item.uuid}
                                            key={item.uuid}
                                            index={index}
                                            name={item.name}
                                            type={MediaItemType.File}
                                            thumbnail={item.thumbnail}
                                            extension={item.extension}
                                            selected={isSelected}
                                            onClick={onClick}
                                            connections={item.connections}
                                            multi={multi}
                                            onToggle={() => {
                                                onToggle({
                                                    type: MediaItemType.File,
                                                    id  : item.uuid,
                                                    name: item.name,
                                                    index,
                                                });
                                            }}
                                        />
                                    );
                                }
                            }
                        }

                        return (
                            <div
                                key={item.key}
                                className="absolute top-0 left-0 w-full grid"
                                style={{
                                    height             : `${item.size}px`,
                                    transform          : `translateY(${item.start}px)`,
                                    gridTemplateColumns: small ? (
                                        `repeat(${columns}, minmax(100px, 1fr))`
                                    ) : (
                                        `repeat(${columns}, minmax(200px, 1fr))`
                                    ),
                                }}
                            >
                                {content}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
